import { Component, Inject, Optional, type OnInit, signal, inject, PLATFORM_ID, type OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SafePipe } from '../../pipes/dom-sanitizer.pipe';
import { PaymentMethod } from '../../models/payment-method';
import { DialogResultType } from '../../enums/dialogresulttype.enum';
import { PaymentDialogResult } from '../../models/payment-dialog-result';
import { MatButtonModule } from '@angular/material/button';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../services/cms.service';
import { environment } from '../../../../environments/environment';
import { NotificationComponent } from '../notification/notification.component';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from '../../services/notifications.service';
import { NotificationType } from '../../enums/notificationtype.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { DigitalPaymentBrowserService } from '../../services/digital-payment-browser.service';
import { PaymentAccountType } from '../../enums/paymentaccounttype.enum';
import { StorageService } from '../../services/storage.service';
import { StorageEnum } from '../../enums/storage.enum';

@Component({
  selector: 'app-digital-payment-modal',
  imports: [SafePipe, MatButtonModule, NotificationComponent],
  templateUrl: './digital-payment-modal.component.html',
  styleUrl: './digital-payment-modal.component.scss',
})
export class DigitalPaymentModalComponent implements OnInit, OnDestroy {
  pageData: any;
  logging: string = '';
  pageLabels: any;
  pageReady = false;
  deviceInfo = environment.deviceInfo;
  showIframe = true;
  pageName = 'digital-payment-modal';
  /** Signal indicating if this is a digital wallet payment (Google Pay, Apple Pay, PayPal, Venmo) */
  readonly isDigitalWallet = signal(false);
  /** Signal indicating if the in-app browser is currently opening/open */
  readonly openingInBrowser = signal(false);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly digitalPaymentBrowser = inject(DigitalPaymentBrowserService);
  private readonly storage = inject(StorageService);

  constructor(
    @Optional() public dialogRef: MatDialogRef<DigitalPaymentModalComponent>,
    private readonly cms: CmsService,
    @Inject(MAT_DIALOG_DATA) data: any,
    private readonly notifiS: NotificationsService,
  ) {
    this.pageData = data;
  }

  ngOnInit(): void {
    this.cms.getContent('oas-digital-payment', 'OASDigitalPaymentModal').subscribe((data) => {
      this.pageLabels = data;
      this.checkPaymentType();
      this.pageReady = true;
    });
    this.logging += JSON.stringify(this.pageData) + '\n';

    // Set up postMessage listener for web iFrame flow
    if (this.isBrowser) {
      window.addEventListener('message', this.handlePostMessage);
    }
  }

  ngOnDestroy(): void {
    // Clean up postMessage listener
    if (this.isBrowser) {
      window.removeEventListener('message', this.handlePostMessage);
    }
  }

  /**
   * Handle postMessage events from the Paymentus iFrame (web flow)
   */
  private readonly handlePostMessage = (e: MessageEvent): void => {
    this.logging += JSON.stringify(e) + '\n';
    const pmDetails = e.data;

    if (typeof pmDetails === 'string' && pmDetails?.includes('Token')) {
      const pmDetailsJson = JSON.parse(pmDetails.substring(pmDetails.indexOf(':') + 1));
      this.logging += JSON.stringify(pmDetailsJson) + '\n';
      this.addPaymentMethod(pmDetailsJson.Token, pmDetailsJson.Type);
    }
  };

  /**
   * Determine whether to show iFrame or open in-app browser based on payment type and platform.
   * Apple Pay requires iOS or Mac+Safari and works on both native (in-app browser) and web (iFrame).
   */
  checkPaymentType(): void {
    const paymentType = this.pageData.paymentAccountType;

    // Check if this is a digital wallet payment type
    const digitalWalletTypes = [
      PaymentAccountType.GooglePay,
      PaymentAccountType.ApplePay,
      PaymentAccountType.PayPal,
      PaymentAccountType.Venmo,
      'Google Pay',
      'Apple Pay',
      'PayPal',
      'Venmo',
    ];

    const isDigitalWallet = digitalWalletTypes.some(
      (type) => paymentType === type || paymentType?.toString() === type.toString(),
    );

    this.isDigitalWallet.set(isDigitalWallet);

    const isApplePay = paymentType === PaymentAccountType.ApplePay || paymentType === 'Apple Pay';

    // Apple Pay requires iOS or Mac+Safari
    const isApplePaySupported =
      (this.deviceInfo as any)?.os === 'iOS' ||
      ((this.deviceInfo as any)?.os === 'Mac' && (this.deviceInfo as any)?.browser === 'Safari');

    if (isApplePay) {
      if (!isApplePaySupported) {
        // Apple Pay not supported on this device/browser
        this.showIframe = false;
        this.notifiS.addNotification(this.pageLabels.applePayNotAllowedMessage, NotificationType.Alert, this.pageName);
      } else if (this.digitalPaymentBrowser.isInAppBrowserSupported()) {
        // Native iOS - use in-app browser
        this.showIframe = false;
        void this.openDigitalPaymentInBrowser();
      } else {
        // Web (iOS Safari or Mac+Safari) - use iFrame
        this.showIframe = true;
      }
    } else if (isDigitalWallet && this.digitalPaymentBrowser.isInAppBrowserSupported()) {
      // Other digital wallets (Google Pay, PayPal, Venmo) on native platforms - use in-app browser
      this.showIframe = false;
      void this.openDigitalPaymentInBrowser();
    } else {
      // Web platform or non-digital wallet - use iFrame
      this.showIframe = true;
    }

    if (this.showIframe) {
      this.notifiS.clearAllNotifications(this.pageName);
    }
  }

  /**
   * Opens the digital payment flow in an in-app browser (for iOS/Android native).
   * This method handles Google Pay, Apple Pay, PayPal, and Venmo.
   */
  async openDigitalPaymentInBrowser(): Promise<void> {
    this.openingInBrowser.set(true);
    this.notifiS.clearAllNotifications(this.pageName);
    const returnRoute = this.getReturnRouteForCallback();

    if (returnRoute) {
      this.storage.setSession(StorageEnum.DigitalPaymentReturnRoute, returnRoute);
    }

    try {
      const result = await this.digitalPaymentBrowser.openPaymentBrowser({
        url: this.pageData.url,
        paymentAccountType: this.pageData.paymentAccountType,
        callbackUrlPattern: this.pageData.callbackUrlPattern || DigitalPaymentBrowserService.DEFAULT_CALLBACK_PATTERN,
      });

      this.openingInBrowser.set(false);
      this.logging += 'Browser result: ' + JSON.stringify(result) + '\n';
      if (result.success && result.token) {
        // Payment successful - add the payment method
        this.addPaymentMethod(result.token, result.type);
      } else if (result.cancelled) {
        this.storage.removeSession(StorageEnum.DigitalPaymentReturnRoute);
        // User cancelled - close dialog without adding payment
        this.closeModal();
      } else {
        // Error occurred
        const errorMessage =
          result.error ??
          this.pageLabels?.digitalPaymentErrorMessage ??
          'Unable to complete payment. Please try again.';
        this.notifiS.addNotification(errorMessage, NotificationType.Alert, this.pageName);
      }
    } catch (error) {
      this.logging += 'InAppBrowser error: ' + JSON.stringify(error) + '\n';
      this.openingInBrowser.set(false);
      this.storage.removeSession(StorageEnum.DigitalPaymentReturnRoute);
      this.notifiS.addNotification(
        this.pageLabels?.digitalPaymentErrorMessage ?? 'Unable to open payment. Please try again.',
        NotificationType.Alert,
        this.pageName,
      );
    }
  }

  private getReturnRouteForCallback(): string | null {
    const page = this.pageData?.page;
    if (typeof page !== 'string' || !page.trim()) {
      return null;
    }

    const trimmedPage = page.trim();
    if (trimmedPage.startsWith('/')) {
      return trimmedPage;
    }

    try {
      const parsedUrl = new URL(trimmedPage);
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return `/${trimmedPage}`;
    }
  }

  /**
   * Creates a payment method from the token and closes the dialog
   */
  addPaymentMethod(token: string, type?: string): void {
    const digitalMethod = new PaymentMethod();
    digitalMethod.ReferenceId = token;
    digitalMethod.PaymentAccountType = this.pageData.paymentAccountType;
    digitalMethod.Description = this.pageData.paymentAccountType;
    digitalMethod.SaveToAccount = true;
    digitalMethod.BankCardDigital = 'DIGITAL';

    const dialogResult = new PaymentDialogResult();
    dialogResult.PayMethod = digitalMethod;
    dialogResult.Type = DialogResultType.Add;
    this.dialogRef.close(dialogResult);
  }

  /**
   * Close the modal and clean up resources
   */
  closeModal(): void {
    const dialogResult = new PaymentDialogResult();
    dialogResult.Type = DialogResultType.Cancel;
    this.notifiS.clearAllNotifications(this.pageName);

    // Close InAppBrowser if open
    if (this.isDigitalWallet() && this.openingInBrowser()) {
      void this.digitalPaymentBrowser.closeBrowser();
    }

    this.dialogRef.close(dialogResult);
  }
}
